import React, { useEffect, useState } from 'react';
import TableDisplay from './TableDisplay';
import Orders from './Orders';
import { BACKEND_URL } from '../config';
import OrderScreen from './OrderScreen';
import OrderStatus from './OrderStatus';
import AdminDashboard from './AdminDashboard';
import RegisterManagement from './Register/RegisterManagement';
import PrintTestModal from './Shared/PrintTestModal';
import { useLanguage } from '../contexts/LanguageContext';
import { usePermissions } from '../contexts/PermissionsContext';
import ErrorBoundary from './ErrorBoundary';


export default function ServerScreen({ user, theme, table, onThemeChange, onBrandingChange }) {
    const { t } = useLanguage();
    const { getDefaultScreen, getDefaultFilters, showAdminDashboard: canShowAdmin, showServerDashboard: canShowServer } = usePermissions();
    const [tableInfo, setTableInfo] = useState(table);
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeOrderId, setActiveOrderId] = useState(null);
    const [showOrderStatus, setShowOrderStatus] = useState(false);
    const [showAdminDashboard, setShowAdminDashboard] = useState(false);
    const [showRegisterManagement, setShowRegisterManagement] = useState(false);
    const [showServerDashboard, setShowServerDashboard] = useState(true);
    const [showPrintTestModal, setShowPrintTestModal] = useState(false);
    const [isWideScreen, setIsWideScreen] = useState(window.innerWidth >= 768);
    const [floorPlanSettings, setFloorPlanSettings] = useState({ floorPlanWidth: 600, floorPlanHeight: 400, gridSnapSize: 0 });
    const [kitchenOutputMode, setKitchenOutputMode] = useState('display');

    useEffect(() => {
        const handleResize = () => setIsWideScreen(window.innerWidth >= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Auto-redirect based on user permissions on mount
    useEffect(() => {
        const defaultScreen = getDefaultScreen();
        if (defaultScreen === 'orderStatus' && (kitchenOutputMode === 'display' || kitchenOutputMode === 'both')) {
            setShowOrderStatus(true);
            setShowServerDashboard(false);
            setShowAdminDashboard(false);
        }
    }, [getDefaultScreen, canShowAdmin, canShowServer, getDefaultFilters, kitchenOutputMode]);

    // Helper function to check if navigation is allowed (asks user if preorder items exist)
    const checkNavigationAllowed = () => {
        if (activeOrderId) {
            const event = new CustomEvent('requestNavigateAway', { cancelable: true });
            const allowed = window.dispatchEvent(event);
            return allowed; // Returns false if OrderScreen prevented navigation
        }
        return true;
    };

    // Listen for logo click to navigate to default screen
    useEffect(() => {
        const handleNavigateToDefault = () => {
            // Check if navigation is allowed
            if (!checkNavigationAllowed()) return;

            const defaultScreen = getDefaultScreen();
            if (defaultScreen === 'orderStatus' && (kitchenOutputMode === 'display' || kitchenOutputMode === 'both')) {
                setShowOrderStatus(true);
                setShowServerDashboard(false);
                setShowAdminDashboard(false);
            } else {
                setShowServerDashboard(true);
                setShowOrderStatus(false);
                setShowAdminDashboard(false);
            }
            setShowRegisterManagement(false);
            setActiveOrderId(null);
            setTableInfo(null);
            fetchFloorPlanSettings();
        };

        window.addEventListener('navigateToDefaultScreen', handleNavigateToDefault);
        return () => window.removeEventListener('navigateToDefaultScreen', handleNavigateToDefault);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getDefaultScreen, activeOrderId]);

    async function fetchFloorPlanSettings() {
        try {
            const response = await fetch(`${BACKEND_URL}/api/system-settings`);
            if (response.ok) {
                const data = await response.json();
                setFloorPlanSettings({
                    floorPlanWidth: parseInt(data.floorPlanWidth) || 600,
                    floorPlanHeight: parseInt(data.floorPlanHeight) || 400,
                    gridSnapSize: parseInt(data.gridSnapSize) || 0
                });
                // kitchenOutputMode: 'display', 'tickets', 'both', or 'none'
                const mode = data.kitchenOutputMode || 'display';
                setKitchenOutputMode(mode);
                // If KDS is not visible in this mode and we're currently viewing it, redirect
                if (mode === 'tickets' || mode === 'none') {
                    setShowOrderStatus(false);
                    setShowServerDashboard(true);
                }
            }
        } catch (err) {
            console.error('Error fetching floor plan settings:', err);
        }
    }

    useEffect(() => {
        async function fetchTables() {
            try {
                const response = await fetch(`${BACKEND_URL}/api/tables`);
                if (!response.ok) throw new Error(t('error') + ': Failed to fetch tables');
                const data = await response.json();
                setTables(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }
        fetchTables();
        fetchFloorPlanSettings();
    }, [t]);

    return (
        <div
            className="server-screen"
            style={{
                background: theme?.colors?.background || '#f5f5f5',
                color: theme?.colors?.text || '#000',
                fontFamily: theme?.fonts?.main || 'sans-serif',
                minHeight: isWideScreen ? '100vh' : 'auto',
                padding: '0.5rem',
            }}
        >
            {/* Toggle button for Order Status view */}
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'stretch',
                gap: '0.5rem',
                marginBottom: '1rem',
                flexWrap: 'wrap'
            }}>
                {canShowServer() && (
                    <button
                        onClick={() => {
                            if (!checkNavigationAllowed()) return;
                            setShowServerDashboard(true);
                            setShowOrderStatus(false);
                            setShowAdminDashboard(false);
                            setShowRegisterManagement(false);
                            setActiveOrderId(null);
                            setTableInfo(null);
                            fetchFloorPlanSettings();
                        }}
                        style={{
                            flex: '1 1 200px',
                            padding: '0.75rem 1rem',
                            backgroundColor: showServerDashboard ? (theme?.colors?.secondary || '#666') : (theme?.colors?.primary || '#1976d2'),
                            color: theme?.colors?.surface || '#fff',
                            border: 'none',
                            borderRadius: theme.borderRadius || 8,
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            boxShadow: theme.boxShadow,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '60px'
                        }}
                        title={t('dashboard')}
                    >
                        {t('server')} {t('dashboard')}
                    </button>
                )}

                {canShowAdmin() && (
                    <button
                        onClick={() => {
                            if (!checkNavigationAllowed()) return;
                            setShowServerDashboard(false);
                            setShowOrderStatus(false);
                            setShowAdminDashboard(true);
                            setShowRegisterManagement(false);
                            setActiveOrderId(null);
                            setTableInfo(null);
                        }}
                        style={{
                            flex: '1 1 200px',
                            padding: '0.75rem 1rem',
                            backgroundColor: showAdminDashboard ? (theme?.colors?.secondary || '#666') : (theme?.colors?.primary || '#1976d2'),
                            color: theme?.colors?.surface || '#fff',
                            border: 'none',
                            borderRadius: theme.borderRadius || 8,
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            boxShadow: theme.boxShadow,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '60px'
                        }}
                        title={t('administration')}
                    >
                        🛠️ {t('admin')} {t('dashboard')}
                    </button>
                )}
{/* 
                {canShowAdmin() && (
                    <button
                        onClick={() => {
                            setShowServerDashboard(false);
                            setShowOrderStatus(false);
                            setShowAdminDashboard(false);
                            setShowRegisterManagement(true);
                            setActiveOrderId(null);
                            setTableInfo(null);
                        }}
                        style={{
                            flex: '1 1 200px',
                            padding: '0.75rem 1rem',
                            backgroundColor: showRegisterManagement ? (theme?.colors?.secondary || '#666') : (theme?.colors?.primary || '#1976d2'),
                            color: theme?.colors?.surface || '#fff',
                            border: 'none',
                            borderRadius: theme.borderRadius || 8,
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            boxShadow: theme.boxShadow,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '60px'
                        }}
                        title={t('registerManagement')}
                    >
                        💰 {t('registerManagement')}
                    </button>
                )} */}

                {(kitchenOutputMode === 'display' || kitchenOutputMode === 'both') && (
                <button
                    onClick={() => {
                        if (!checkNavigationAllowed()) return;
                        setShowServerDashboard(false);
                        setShowOrderStatus(true);
                        setShowAdminDashboard(false);
                        setShowRegisterManagement(false);
                        setActiveOrderId(null);
                        setTableInfo(null);
                    }}
                    style={{
                        flex: '1 1 200px',
                        padding: '0.75rem 1rem',
                        backgroundColor: showOrderStatus ? (theme?.colors?.secondary || '#666') : (theme?.colors?.primary || '#1976d2'),
                        color: theme?.colors?.surface || '#fff',
                        border: 'none',
                        borderRadius: theme.borderRadius || 8,
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        boxShadow: theme.boxShadow,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '60px'
                    }}
                >
                    {t('orderStatus')}
                </button>
                )}

                {user?.user_name?.toLowerCase() === 'testdev' && (
                    <button
                        onClick={() => setShowPrintTestModal(true)}
                        style={{
                            flex: '1 1 200px',
                            padding: '0.75rem 1rem',
                            backgroundColor: theme?.colors?.warning || '#ff9800',
                            color: theme?.colors?.surface || '#fff',
                            border: 'none',
                            borderRadius: theme.borderRadius || 8,
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            boxShadow: theme.boxShadow,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '60px'
                        }}
                        title={t('printerTestMode')}
                    >
                        🖨️ {t('printTest')}
                    </button>
                )}
            </div>
            
            {showOrderStatus && (kitchenOutputMode === 'display' || kitchenOutputMode === 'both') ? (
                <OrderStatus theme={theme} defaultFilters={getDefaultFilters()} />
            ) : showAdminDashboard ? (
                <AdminDashboard theme={theme} onThemeChange={onThemeChange} onBrandingChange={onBrandingChange} user={user} />
            ) : showRegisterManagement ? (
                <RegisterManagement theme={theme} user={user} />
            ) : activeOrderId ? (
                <ErrorBoundary onClose={() => {
                    setShowServerDashboard(true);
                    setShowOrderStatus(false);
                    setShowAdminDashboard(false);
                    setShowRegisterManagement(false);
                    setActiveOrderId(null);
                    setTableInfo(null);
                }}>
                    <OrderScreen
                        orderId={activeOrderId}
                        theme={theme}
                        user={user}
                        onClose={() => {
                            setShowServerDashboard(true);
                            setShowOrderStatus(false);
                            setShowAdminDashboard(false);
                            setShowRegisterManagement(false);
                            setActiveOrderId(null);
                            setTableInfo(null);
                        }}
                    />
                </ErrorBoundary>
            ) : loading ? (
                <p>{t('loading')} {t('tables').toLowerCase()}...</p>
            ) : error ? (
                <p style={{ color: 'red' }}>{t('error')}: {error}</p>
            ) : (
                <div style={{
                    display: 'flex',
                    flexDirection: isWideScreen ? 'row' : 'column',
                    gap: isWideScreen ? '1rem' : '0.25rem',
                    alignItems: 'flex-start',
                }}>
                    <div
                        style={{
                            ...(isWideScreen ? {
                                flex: '1 1 50%',
                                overflowX: 'hidden',
                            } : {
                                width: '100%',
                                marginBottom: '0',
                            }),
                        }}
                    >
                        <Orders tables={tables} theme={theme} onOrderClick={setActiveOrderId} user={user} />
                    </div>
                    <div style={{
                        flex: isWideScreen ? '0 0 33%' : 'none',
                        minWidth: 0,
                        width: isWideScreen ? undefined : '100%',
                        display: isWideScreen ? undefined : 'flex',
                        justifyContent: isWideScreen ? undefined : 'center',
                    }}>
                        <TableDisplay
                            tables={tables}
                            theme={theme}
                            onOrderSelect={setActiveOrderId}
                            tableInfo={tableInfo}
                            setTableInfo={setTableInfo}
                            user={user}
                            floorPlanWidth={floorPlanSettings.floorPlanWidth}
                            floorPlanHeight={floorPlanSettings.floorPlanHeight}
                            gridSnapSize={floorPlanSettings.gridSnapSize}
                            onFloorPlanResize={(newWidth, newHeight) => {
                                setFloorPlanSettings(prev => ({
                                    ...prev,
                                    floorPlanWidth: newWidth,
                                    floorPlanHeight: newHeight
                                }));
                                fetch(`${BACKEND_URL}/api/system-settings`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                        floorPlanWidth: String(newWidth),
                                        floorPlanHeight: String(newHeight)
                                    })
                                }).catch(err => console.error('Error saving floor plan size:', err));
                            }}
                        />
                    </div>
                </div>
            )}

            {showPrintTestModal && (
                <PrintTestModal onClose={() => setShowPrintTestModal(false)} />
            )}
        </div>
    );
}
